import { useEffect, useMemo, useRef } from "react";

import { Moment } from "@common/utils/libs";
import _uniq from "lodash/uniq";

import { useApolloClient } from "@plco-pro/graphqls/provider";
import {
  AnalyticsViewerDocument,
  AnalyticsViewerQuery,
  AnalyticsViewerQueryVariables,
  Gender,
  SportsTeamAuthority,
} from "@plco-pro/graphqls/react.generated";
import { useAnalytics } from "@plco-pro/hooks/analytics";
import { useMoment } from "@plco-pro/hooks/moment";
import { useAnalyticsContext } from "@plco-pro/providers/analytics";
import { useStore } from "@plco-pro/stores";

import { LANGUAGE_MAP } from "./multilingual";
import { useViewer } from "./viewer/viewer";

export enum AnalyticsUserLanguage {
  KOREAN = "Korean",
  JAPANESE = "Japanese",
  ENGLISH = "English",
  VIETNAMESE = "Vietnamese",
}

export interface IdentifyData {
  // segment reserved traits
  id?: string;
  name: string;
  email: string;
  age: number | null;
  gender: Gender | null;
  authority: string[];
  language: string;
  phoneNumber: string;
  teamslug_name: string;
  connected_team_count: number;
  test_account_exclusion: boolean;
  team_joined: boolean;
}

const parseIdentifyData = (
  analyticsViewerData: AnalyticsViewerQuery,
  moment: Moment,
  language: AnalyticsUserLanguage,
  teamslug_name: string
): IdentifyData => {
  const { viewer } = analyticsViewerData.sports;

  return {
    id: viewer?.id,
    name: viewer?.name || "",
    email: viewer?.email || "",
    age: moment().diff(viewer?.birthdate, "years"),
    gender: viewer?.gender || null,
    authority: _uniq(
      (viewer?.teamMaps || []).map((teamMap) => teamMap.teamAuthority || "")
    ),
    language,
    phoneNumber: viewer?.phoneNumber || "",
    teamslug_name,
    test_account_exclusion: !!viewer?.email?.endsWith("@qmit.kr"),
    connected_team_count: (viewer?.teamMaps || []).length,
    team_joined: !!viewer?.teamMaps.filter(
      ({ teamAuthority }) =>
        teamAuthority === SportsTeamAuthority.ADMIN ||
        teamAuthority === SportsTeamAuthority.OBSERVER ||
        teamAuthority === SportsTeamAuthority.OWNER
    ).length,
  };
};

export const useIdentify = () => {
  const moment = useMoment();
  const { identifyUser } = useAnalytics();
  const { setIsIdentified } = useAnalyticsContext();
  const { preference } = useStore();
  const { currentActiveTeam } = useViewer();

  const teamslug_name = `${currentActiveTeam?.slug}_${
    currentActiveTeam?.name || ""
  }`;

  const languageCode = preference.locale.language.split(
    "-"
  )[0] as keyof typeof LANGUAGE_MAP;

  // 사용 중인 언어
  const language = useMemo(() => {
    if (languageCode === "ko") {
      return AnalyticsUserLanguage.KOREAN;
    }

    if (languageCode === "ja") {
      return AnalyticsUserLanguage.JAPANESE;
    }

    if (languageCode === "en") {
      return AnalyticsUserLanguage.ENGLISH;
    }

    return AnalyticsUserLanguage.VIETNAMESE;
  }, [languageCode]);

  const analyticsViewerData = useApolloClient().readQuery<
    AnalyticsViewerQuery,
    AnalyticsViewerQueryVariables
  >({ query: AnalyticsViewerDocument });
  const parsedDataRef = useRef<IdentifyData>();

  useEffect(() => {
    if (analyticsViewerData && analyticsViewerData.sports.viewer) {
      const newParseIdentifyData = parseIdentifyData(
        analyticsViewerData,
        moment,
        language,
        teamslug_name
      );

      if (
        JSON.stringify(newParseIdentifyData) !==
        JSON.stringify(parsedDataRef.current)
      ) {
        parsedDataRef.current = newParseIdentifyData;
        if (parsedDataRef.current.id) {
          identifyUser(parsedDataRef.current.id, parsedDataRef.current as any);
        }
      }
    } else {
      setIsIdentified(false);
    }
  }, [
    analyticsViewerData,
    moment,
    identifyUser,
    setIsIdentified,
    language,
    teamslug_name,
  ]);

  return {
    data: parsedDataRef.current,
  };
};
